import { User, UsersThree, Buildings, Cube, IdentificationCard } from "@phosphor-icons/react";
import { PickerItemProps } from "../../types";

// Resource types matching backend TaskResourceType enum
export type ResourceType =
  | 'general'
  | 'building'
  | 'unit'
  | 'helpdesk'
  | 'renovation'
  | 'rentalfile'
  | 'rentalcontract'
  | 'contact'
  | 'company'
  | 'internal_company';

export interface ResourceData {
  id: number | string;
  resourceId: string;
  type: ResourceType;
  name: string;
  metadata1?: string;
  metadata2?: string;
}

const getResourceIcon = (type: ResourceType) => {
  switch (type) {
    case 'contact':
      return <User className="h-4 w-4" weight="duotone" />;
    case 'company':
    case 'internal_company':
      return <UsersThree className="h-4 w-4" weight="duotone" />;
    case 'building':
      return <Buildings className="h-4 w-4" weight="duotone" />;
    case 'unit':
      return <Cube className="h-4 w-4" weight="duotone" />;
    case 'rentalcontract':
      return <IdentificationCard className="h-4 w-4" weight="duotone" />;
    default:
      return <IdentificationCard className="h-4 w-4" weight="duotone" />;
  }
};

const getResourceTypeLabel = (type: ResourceType): string => {
  switch (type) {
    case 'contact':
      return 'Contact';
    case 'company':
      return 'Bedrijf';
    case 'internal_company':
      return 'Vennootschap';
    case 'building':
      return 'Gebouw';
    case 'unit':
      return 'Unit';
    case 'rentalcontract':
      return 'Huurcontract';
    case 'helpdesk':
      return 'Helpdesk';
    case 'renovation':
      return 'Renovatie';
    default:
      return 'Resource';
  }
};

const getResourceTypeColor = (type: ResourceType): string => {
  switch (type) {
    case 'contact':
      return 'bg-blue-100 text-blue-700';
    case 'company':
    case 'internal_company':
      return 'bg-purple-100 text-purple-700';
    case 'building':
      return 'bg-amber-100 text-amber-700';
    case 'unit':
      return 'bg-green-100 text-green-700';
    case 'rentalcontract':
      return 'bg-pink-100 text-pink-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

interface ResourceItemProps extends PickerItemProps<ResourceData> {
  showCheckbox?: boolean;
}

export const ResourceItem = ({
  item,
  isSelected,
  onSelect,
  showCheckbox = false
}: ResourceItemProps) => {
  const icon = getResourceIcon(item.type);
  const typeLabel = getResourceTypeLabel(item.type);
  const typeColorClass = getResourceTypeColor(item.type);

  return (
    <button
      onClick={() => onSelect(item)}
      className={`w-full text-left px-4 py-3 rounded-lg text-sm transition-all duration-200 border ${
        isSelected
          ? "bg-moss-primary/10 border-moss-primary/40 ring-2 ring-moss-primary/20 shadow-sm"
          : "border-neutral-primary/10 hover:border-neutral-primary/20 hover:bg-neutral-primary/5 hover:shadow-sm"
      }`}
    >
      <div className="flex items-center gap-3">
        {showCheckbox && (
          <div className="shrink-0">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => {}}
              className="w-4 h-4 rounded border-neutral-300 text-moss-primary focus:ring-moss-primary cursor-pointer"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        <div className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
          isSelected ? 'bg-moss-primary/20 text-moss-primary' : 'bg-neutral-primary/5 text-neutral-primary/70'
        }`}>
          {icon}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="font-semibold text-neutral-primary truncate flex-1">
              {item.name}
            </span>
            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full shrink-0 ${typeColorClass}`}>
              {typeLabel}
            </span>
          </div>

          {item.metadata1 && (
            <div className="text-xs text-neutral-secondary-mid truncate">
              {item.metadata1}
            </div>
          )}

          {item.metadata2 && (
            <div className="text-xs text-neutral-secondary-mid/70 truncate mt-0.5">
              {item.metadata2}
            </div>
          )}
        </div>
      </div>
    </button>
  );
};

// Chip component for displaying selected resources in a compact format
interface ResourceChipProps {
  resource: ResourceData;
  onRemove: () => void;
}

export const ResourceChip = ({ resource, onRemove }: ResourceChipProps) => {
  const icon = getResourceIcon(resource.type);
  const typeColorClass = getResourceTypeColor(resource.type);

  return (
    <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${typeColorClass} border border-current/20`}>
      <span className="shrink-0">{icon}</span>
      <span className="truncate max-w-[120px]">{resource.name}</span>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="shrink-0 ml-0.5 hover:bg-black/10 rounded-full p-0.5 transition-colors"
        aria-label={`Verwijder ${resource.name}`}
      >
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};
